import produce from 'immer';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import { FormTemplateType } from 'shared/types/graphql';
import { Tools } from '../FormsContent/FormsDrafting/AddQuestion';
import { IQuestion } from '../FormsContent/FormsDrafting/Question';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formsReducer = produce((draft: any, action: any) => {
  const toggleHasChanges = (): void => {
    if (!draft.hasChanges) {
      draft.hasChanges = true;
    }
  };
  const onformTemplateRecentlyChanged = (): void => {
    if (!draft.hasFormTemplateRecentlyChanged) {
      draft.hasFormTemplateRecentlyChanged = true;
    }
  };

  const { type, payload } = action;
  const {
    questionIndex,
    questionType,
    optionIndex,
    option,
    minValue,
    maxValue,
    formTemplate,
    isRequired,
    isTemplateSelectorShown,
    hasLinkedAutomations,
  } = payload;

  const hasFillInTheBlanks = (questions: IQuestion[], excludeIndex?: number): boolean =>
    questions.some((question, index) => !!question.hasFillInTheBlanks && index !== excludeIndex);

  switch (type) {
    case 'SET_FORM_ID':
      draft.formId = payload;
      break;
    case 'CHANGE_FORM_NAME':
      draft.formName = payload;
      toggleHasChanges();
      break;
    case 'CHANGE_FORM_AUTO_EXPORT_ENABLED':
      draft.autoExportEnabled = !draft.autoExportEnabled;
      toggleHasChanges();
      break;
    case 'CHANGE_INTRO_MESSAGE':
      draft.introMessage = payload;
      toggleHasChanges();
      const { message } = payload;
      draft.introMessage = message;
      break;
    case 'ADD_QUESTION_AT_INDEX':
      const { index, question } = payload;
      const originalArray = [...draft.questions];
      const leftArray = originalArray.slice(0, index);
      const rightArray = originalArray.slice(index, originalArray.length);
      draft.questions = [...leftArray, question, ...rightArray];
      toggleHasChanges();
      onformTemplateRecentlyChanged();
      break;
    case 'REMOVE_QUESTION_AT_INDEX':
      draft.questions.splice(payload.index, 1);
      toggleHasChanges();
      onformTemplateRecentlyChanged();
      draft.formTemplateType =
        hasFillInTheBlanks(draft.questions, payload.index) || draft.introMessage.includes(WildcardValues.FillInTheBlank)
          ? FormTemplateType.FillInTheBlank
          : payload.formTemplateType === FormTemplateType.FillInTheBlank
          ? FormTemplateType.General
          : payload.formTemplateType;
      break;
    case 'CHANGE_PROMPT_AT_INDEX':
      draft.formTemplateType =
        payload.formTemplateType === FormTemplateType.FillInTheBlank
          ? FormTemplateType.General
          : payload.formTemplateType;
      draft.questions[payload.index].prompt = payload.prompt;
      toggleHasChanges();
      draft.questions[payload.index].hasFillInTheBlanks = false;
      if (
        typeof payload?.prompt === 'string' &&
        payload.prompt.includes(WildcardValues.FillInTheBlank) &&
        payload.allowFillInTheBlankWildcards
      ) {
        draft.formTemplateType = FormTemplateType.FillInTheBlank;
        draft.questions[payload.index].hasFillInTheBlanks = true;
      } else if (
        hasFillInTheBlanks(draft.questions, payload.index) ||
        draft.introMessage.includes(WildcardValues.FillInTheBlank)
      ) {
        draft.formTemplateType = FormTemplateType.FillInTheBlank;
      }
      break;
    case 'CHANGE_OPTION_VALUE_AT_INDEX':
      draft.questions[questionIndex].options[optionIndex] = option;
      toggleHasChanges();
      break;
    case 'ADD_OPTION_AT_INDEX':
      draft.questions[questionIndex].options.push('');
      toggleHasChanges();
      break;
    case 'REMOVE_OPTION_AT_INDEX':
      draft.questions[questionIndex].options.splice(optionIndex, 1);
      onformTemplateRecentlyChanged();
      toggleHasChanges();
      break;
    case 'TOGGLE_DEFAULT_AT_INDEX':
      draft.questions[questionIndex].defaultValue = !draft.questions[questionIndex].defaultValue;
      toggleHasChanges();
      break;
    case 'CHANGE_MIN_VALUE_AT_INDEX':
      draft.questions[questionIndex].minValue = minValue;
      toggleHasChanges();
      break;
    case 'CHANGE_MAX_VALUE_AT_INDEX':
      draft.questions[questionIndex].maxValue = maxValue;
      toggleHasChanges();
      break;
    case 'CHANGE_QUESTION_TYPE_AT_INDEX':
      const oldPrompt = draft.questions[questionIndex].prompt;
      draft.questions[questionIndex] = { ...Tools[questionType].data };
      draft.questions[questionIndex].prompt = oldPrompt;
      toggleHasChanges();
      break;
    case 'MOVE_QUESTION_AT_INDEX_UP':
      const previousQuestion = draft.questions[questionIndex - 1];
      const thisQuestion = draft.questions[questionIndex];
      draft.questions[questionIndex] = previousQuestion;
      draft.questions[questionIndex - 1] = thisQuestion;
      toggleHasChanges();
      onformTemplateRecentlyChanged();
      break;
    case 'MOVE_QUESTION_AT_INDEX_DOWN':
      const nextQuestion = draft.questions[questionIndex + 1];
      const currentQuestion = draft.questions[questionIndex];
      draft.questions[questionIndex] = nextQuestion;
      draft.questions[questionIndex + 1] = currentQuestion;
      toggleHasChanges();
      onformTemplateRecentlyChanged();
      break;
    case 'SELECT_FORM_TEMPLATE':
      const { id, title, content, isActive, formTemplateType, clinicWidgetRequestTypes, autoExportEnabled } =
        formTemplate;
      const { introMessage, questions } = content || {};
      draft.formId = id;
      draft.isActive = isActive;
      draft.formName = title;
      draft.introMessage = introMessage || '';
      draft.questions = questions || [];
      draft.formTemplateType = formTemplateType;
      draft.autoExportEnabled = autoExportEnabled;
      draft.clinicWidgetRequestTypes = clinicWidgetRequestTypes || [];
      draft.original = {
        title,
        introMessage: introMessage || '',
        questions: questions || [],
        autoExportEnabled: autoExportEnabled,
      };
      draft.hasChanges = false;
      draft.hasLinkedAutomations = hasLinkedAutomations;
      break;
    case 'UPDATE_IS_REQUIRED_AT_INDEX':
      draft.questions[questionIndex].isRequired = isRequired;
      toggleHasChanges();
      break;
    case 'RESET_TEMPLATE_CHANGE':
      draft.hasFormTemplateRecentlyChanged = false;
      break;
    case 'RESET_CONTEXT':
      draft.formId = '';
      draft.hasChanges = false;
      draft.isNewForm = false;
      break;
    case 'TOGGLE_TEMPLATE_SELECTOR':
      draft.isTemplateSelectorShown = isTemplateSelectorShown;
      break;
    case 'RESET_FORM':
      if (draft.original) {
        draft.formName = draft.original.title;
        draft.introMessage = draft.original.introMessage || '';
        draft.questions = draft.original.questions || [];
        draft.hasChanges = false;
        draft.hasFormTemplateRecentlyChanged = false;
        draft.autoExportEnabled = draft.original.autoExportEnabled;
      }
      break;
    case 'RESET_HAS_CHANGES':
      draft.hasChanges = false;
      draft.hasFormTemplateRecentlyChanged = false;
      break;
    case 'ADD_NEW_FORM':
      draft.isNewForm = true;
      draft.isTemplateSelectorShown = false;
      break;
    case 'RESET_NEW_FORM':
      draft.isNewForm = false;
      break;
    default:
      break;
  }
});
